import * as React from "react";
import * as ReactDOM from "react-dom";
import "core-js/stable";
import "regenerator-runtime/runtime";
import { initializeIcons, Spinner } from "@fluentui/react";
import { initializeFileTypeIcons } from "@fluentui/react-file-type-icons";
import { Provider } from "react-redux";
import { LanguageContext, LocalizationModule } from "../utilities/LocalizationModule";
import "../src/taskpane/styles/taskpane.css";
import "../src/taskpane/styles/custom.css";
import "react-toastify/dist/ReactToastify.css";
import dayjs from 'dayjs';
import 'dayjs/locale/nl'; // import locale
import 'dayjs/locale/de';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { IAuthTokenResultDto } from "../src/taskpane/interfaces/IAuthTokenResultDto";
import authModule from "../utilities/AuthModule";
import { storeMain } from "../src/taskpane/components/_v2/redux/storeMain";
import { ContentManager } from "./ContentManager";
import generalModule, { AppModeEnum } from "../utilities/GeneralModule";
import { ModeEnum } from "../src/taskpane/components/enums/modeEnum";
import { contentManager, shared } from "../src/taskpane/config/constants";
import { StorageHelper } from "../utilities/StorageHelper";
dayjs.extend(localizedFormat);

initializeIcons();
initializeFileTypeIcons();

const appContainer = document.getElementById("container");
appContainer.addEventListener("dragenter", (ev) => { ev.preventDefault(); });
appContainer.addEventListener("dragover", (ev) => { ev.preventDefault(); });
appContainer.addEventListener("drop", (ev) => { ev.preventDefault(); });

const params = new URLSearchParams(window.location.search);
const lang: string = params.get(shared.params.language) ?? StorageHelper.getItem(shared.params.language) ?? navigator.language;
const localeStrings = LocalizationModule.getLocaleStrings(lang);
// Set dayjs locale globally
if (lang?.toLowerCase().startsWith("nl")) {
  dayjs.locale("nl");
}
else if (lang?.toLowerCase().startsWith("de")) {
  dayjs.locale("de");
}
generalModule.setAppMode(AppModeEnum.contentManager);

interface ILoginSpinnerProps {
  showLabel?: boolean;
}
const AuthSpinner: React.FC<ILoginSpinnerProps> = ({ showLabel }) => {
  return (
    <div className="xb-loader-container">
      <Spinner label={showLabel ? (lang.toLowerCase().startsWith("nl") ? "Aanmeldingsverzoek verwerken ..." : "Processing sign-in request ...") : ""} />
    </div>
  );
};

const render = (component: JSX.Element) => {
  ReactDOM.render(
    component,
    appContainer
  );
};

render(<AuthSpinner />);

const redirectToLogin = () => {
  let authUrl: URL = new URL(process.env.baseUri + "/auth.html");
  authUrl.searchParams.append(shared.params.app, "content_manager");
  if (params.has(shared.params.accountId)) {
    authUrl.searchParams.append(shared.params.accountId, params.get(shared.params.accountId));
  }
  if (params.has(shared.params.username)) {
    authUrl.searchParams.append(shared.params.username, params.get(shared.params.username));
  }
  window.location.replace(authUrl);
};

authModule.setSignOutCallback(redirectToLogin);

(async () => {
  const preloadLocation: string = params.has(contentManager.params.preloadLocation) && params.get(contentManager.params.preloadLocation);
  const preloadURL: string = params.has(contentManager.params.preloadURL) && params.get(contentManager.params.preloadURL);
  if (preloadLocation || preloadURL) {
    await Promise.all([
      preloadLocation && StorageHelper.setItemAsync(contentManager.params.preloadLocation, preloadLocation),
      preloadURL && StorageHelper.setItemAsync(contentManager.params.preloadURL, preloadURL),
      StorageHelper.setItemAsync(shared.params.language, lang)
    ]);
  }

  const authState: IAuthTokenResultDto = authModule.getPersistedAuthState();
  const accountId: string = params.get(shared.params.accountId);

  if (!authState?.docubirdToken || !authState?.account || (accountId && accountId !== authState.account.accountId)) {
    redirectToLogin();
    return;
  }
  else if (accountId || preloadLocation || preloadURL) {
    // Reload clean url
    window.location.replace(process.env.contentManagerUrl);
    return;
  }

  render(!authState?.account
    ? <AuthSpinner showLabel={true} />
    : <Provider store={storeMain}>
      <LanguageContext.Provider value={localeStrings}>
        <ContentManager account={authState.account} initialMode={ModeEnum.contentManager} />
      </LanguageContext.Provider>
    </Provider>);
})();